/**
 * ============================================================
 * DESIGN TOKENS — Alif & Lakshita
 * ============================================================
 *
 * The single place the visual language is declared. Sections consume
 * from here and never restate a colour, a face or a grade value.
 *
 * The type FACES live in `src/assets/fonts/fonts.css`, which declares
 * both the @font-face rules and the `--font-*` variables referenced
 * below. That keeps swapping a face to one edit in one file.
 *
 * This design is the first in the fleet to put all content over a
 * single background video. Two consequences show up in these values:
 *
 * 1. Text is white over moving imagery, so contrast varies frame by
 *    frame. The guaranteed minimum is whatever the BRIGHTEST frame
 *    allows. GRADE below is what makes that survivable — it is the
 *    accessibility floor, not decoration. Lowering its opacity raises
 *    the risk that a bright frame washes the type out.
 *
 * 2. The grade is expressed here in CSS rather than baked into the
 *    images, so a future couple's untouched photography inherits the
 *    look automatically instead of needing an offline hand-grade.
 *    Expect it to APPROXIMATE Canva's blend stack rather than
 *    reproduce it, and expect a tuning pass against `_design/pages`.
 */

/* ------------------------------------------------------------------
 * PALETTE — desaturated dark olive, sampled from the design pages
 * ------------------------------------------------------------------ */
export const COLORS = {
  /** deepest shadow; also the page backdrop before the video paints */
  shadow: '#0d1410',
  /**
   * True black, for the footer only. Deeper than `shadow` on purpose: the footer
   * carries Invitato's own credit rather than the couple's content, and closing
   * the invitation on black reads that band as chrome instead of as the last
   * screen of the invitation.
   */
  footer: '#000000',
  /** base olive of the graded footage */
  base: '#141a14',
  /** mid olive — panel fills, selected tab */
  olive: '#2c3a2a',
  oliveLight: '#3a4a33',
  /** highlights that survive the grade */
  highlight: '#6b7a55',

  /** primary type — everything legible sits on white */
  text: '#ffffff',
  /** secondary type: addresses, helper copy */
  textMuted: 'rgba(255, 255, 255, 0.72)',
  /** the placeholder dots the design draws as "...." */
  textFaint: 'rgba(255, 255, 255, 0.45)',

  /** hairline rules, field underlines, pill borders */
  line: 'rgba(255, 255, 255, 0.55)',
  lineFaint: 'rgba(255, 255, 255, 0.28)',

  /** the six-pointed asterisk divider */
  asterisk: 'rgba(233, 236, 226, 0.72)',

  /** translucent light fill: the gift panel's reveal block and active tab */
  veil: 'rgba(255, 255, 255, 0.22)',
  veilStrong: 'rgba(255, 255, 255, 0.34)',
};

/* ------------------------------------------------------------------
 * COLOUR GRADE — applied once by the background layer, inherited by
 * every section that presents its own photograph.
 * ------------------------------------------------------------------
 * `tint` sits over the media as a flat wash; `filter` grades the media
 * itself. Both are needed: the filter alone cannot push the footage
 * green, and the tint alone cannot pull the saturation down.
 */
export const GRADE = {
  /** flat olive wash over the media */
  tintVideo: 'rgba(26, 27, 29, 0.34)',
  tint: 'rgba(26, 27, 29, 0.34)',
  /** grade applied to the media element itself */
  filter: 'saturate(1) contrast(1) brightness(1)',
  /**
   * Extra darkening at top and bottom so type at the edges keeps its contrast
   * whatever frame or photograph is behind it.
   *
   * The top band is deliberately LIGHTER than the bottom. Measured against the
   * escalator photograph the four override sections use: its source pixels average
   * RGB(93,117,77), the filter and tint above already take that to about
   * RGB(46,63,40), and a 0.55 band on top of that erased the bright ceiling strips
   * that are the only thing making the photograph legible as a photograph — the
   * section read as a flat dark panel. The bottom band can stay heavier because
   * the content there is type, not image.
   *
   * Expect to tune this further against `_design/pages`. The PRD anticipates it:
   * a CSS grade approximates Canva's blend stack rather than reproducing it.
   */
  vignette:
    'linear-gradient(to bottom, rgba(13,20,16,0.30) 0%, rgba(13,20,16,0) 24%, rgba(13,20,16,0) 68%, rgba(13,20,16,0.62) 100%)',
};

/* ------------------------------------------------------------------
 * TYPOGRAPHY
 * ------------------------------------------------------------------ */
export const FONTS = {
  display: `var(--font-display)`,
  accent: `var(--font-accent)`,
  script: `var(--font-script)`,
  body: `var(--font-body)`,
};

/**
 * The script face is drawn on a much smaller optical body than the
 * display serif, so a swash capital has to be set far larger than the
 * caps it replaces to sit right. These are the ratios the design uses.
 *
 * BOTH VALUES HERE ARE OPTICAL. Neither has to be correct for the LAYOUT to be
 * correct: `SwashTitle` puts the swash in normal flow, so its advance width is
 * measured from whatever script face is loaded rather than declared here. A font
 * swap can therefore make the swash look too big or too small — fix that by
 * moving `scale` — but it can no longer make the swash collide with the letters
 * beside it.
 *
 * There is deliberately no VERTICAL offset. The swash sits on the baseline,
 * which is defined by the font's own metrics and so is right for every face. A
 * `-0.17em` bottom offset used to live here, and was right for exactly one.
 */
export const SWASH = {
  /**
   * Script size as a multiple of the title's font size. Champignon sits on a
   * smaller optical body than Instrument Serif, so the swash must be set larger
   * merely to match the roman cap height — but only a little. Tuned against
   * design page 1: at 2.1 the capital towers over its line and its flourish
   * runs outside the invitation column; 1.6 puts the cap height just above the
   * roman caps, which is what is drawn.
   *
   * Expect to revisit this — and only this — when the script face changes.
   */
  scale: 1.3,
  /**
   * The leftward hang, so the capital sits proud of the letters it leads the way
   * a drawn swash does.
   *
   * @important applied as a negative left margin ON the script span, so the `em`
   * is of the SWASH's OWN size, not the title's. Divide a title-relative figure
   * by `scale` to convert.
   */
  hang: '-0.09em',
};

export const TYPE_SCALE = {
  /** the four-line cover / wishes title */
  titleHero: { base: '48px', sm: '52px' },
  /** section titles: THE CELEBRATION, YOUR RSVP, WEDDING GIFT */
  titleSection: { base: '30px', sm: '34px' },
  /** couple-info given name */
  nameLarge: { base: '42px', sm: '48px' },
  /** countdown figures */
  figure: { base: '38px', sm: '44px' },
  /** event name, love-story milestone heading */
  headingBlock: { base: '24px', sm: '27px' },
  /** running copy and field labels */
  body: { base: '16px', sm: '16px' },
  /** the scattered italic words, countdown unit labels, addresses */
  small: { base: '14px', sm: '15px' },
};

/* ------------------------------------------------------------------
 * SHARED PROP BUNDLES
 * ------------------------------------------------------------------
 * Spread onto Chakra components so a section never restates the
 * design's type decisions.
 */

/**
 * All-caps display title; pair with <SwashTitle /> for the script capital.
 *
 * @important `lineHeight` must stay at or above ~1.15, and the reason is not
 * taste. A line box smaller than the face's own ascent-plus-descent lets the
 * glyphs overflow their block, and because the block is what the NEXT element is
 * laid out against, that overflow lands on whatever follows. This sat at 1.02 and
 * did exactly that — most visibly in `Section/CoupleInfo/DetailCard`, where the
 * name printed over the line beneath it.
 *
 * 1.02 was survivable only because Instrument Serif's all-caps have no
 * descenders. It is not a property a future face is obliged to share, so the
 * value is set clear of the whole class of problem rather than tuned to one font.
 *
 * A tighter stack for a specific multi-line title is a LOCAL decision — set
 * `lineHeight` on that block and say why, as the cover's hero does. Do not tighten
 * it here, where it applies to titles that have text underneath them.
 */
export const TITLE_PROPS = {
  fontFamily: FONTS.display,
  fontWeight: 400,
  color: COLORS.text,
  // letterSpacing: '120',
  lineHeight: 0.85,
  textAlign: 'center',
};

/** the serif-italic voice: labels, paragraphs, pill text, addresses */
export const ACCENT_PROPS = {
  fontFamily: FONTS.accent,
  fontStyle: 'italic',
  fontWeight: 400,
  color: COLORS.text,
  lineHeight: 1.5,
};

/** roman running copy — parents line, "Dear Mr/Mrs/Ms", field labels */
export const BODY_PROPS = {
  fontFamily: FONTS.body,
  fontWeight: 400,
  color: COLORS.text,
  lineHeight: 1.45,
};

/**
 * THE OUTLINED PILL is deliberately NOT declared here, even though it is part
 * of the visual language and this is where the rest of that language lives.
 *
 * It needs `TEXT_PROPS.body` for its font weight, and `./default-props` — where
 * TEXT_PROPS lives — already imports from this file. Declaring the pill here
 * would form the import cycle that `./default-props` warns about in its own
 * header, which leaves TEXT_PROPS undefined at module-eval time. So the pill
 * has to sit downstream of both files.
 *
 * Its single definition is `BUTTON_PADDING_PROPS` in `./colors`. A near-copy
 * once sat here under the name PILL_PROPS, documented as canonical but imported
 * by nothing — almost certainly because of this same constraint. It was removed
 * rather than wired up; do not reinstate it.
 */

/** label-above-a-hairline field treatment, used by RSVP, wishes and gift */
export const FIELD_PROPS = {
  ...BODY_PROPS,
  bg: 'transparent',
  border: 'none',
  borderBottom: '1px solid',
  borderColor: COLORS.line,
  borderRadius: 0,
  px: 0,
  color: COLORS.text,
  fontSize: 'body',
  fontFamily: FONTS.body,
  _placeholder: { color: COLORS.textFaint },
  _hover: { borderColor: COLORS.text },
  _focus: { borderColor: COLORS.text, boxShadow: 'none' },
  _focusVisible: { borderColor: COLORS.text, boxShadow: 'none' },
};

export const FIELD_LABEL_PROPS = {
  ...BODY_PROPS,
  fontFamily: FONTS.body,
  // fontSize: 'calc(var(--chakra-fontSizes-body) - 2px)',
};

/** horizontal padding every section shares, so the column reads as one piece */
export const SECTION_PX = { base: '24px', sm: '28px' };
